// Client-side prep for "upload a finished slide" artwork. Decks compile to a
// single HTML file with assets base64-inlined, so a raw 3840×2160 PNG export
// (often 8–15MB) would balloon the deck — re-encode to WebP in the browser
// instead (no server-side image processing in this project).

const MAX_W = 3840;
const MAX_H = 2160;
const TARGET = 16 / 9;
const ASPECT_TOLERANCE = 0.015; // ±1.5% of 16:9 still inserts as cover
const SIZE_WARN_BYTES = 6 * 1024 * 1024;

/** Either decode path gives something drawable that reports its own size —
 *  `ImageBitmap` uses width/height, `HTMLImageElement` naturalWidth/Height. */
type DecodedImage = ImageBitmap | HTMLImageElement;

async function decode(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall through */
    }
  }
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode failed'));
    };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

export type EncodedSlideImage = {
  /** The encoded upload — or the ORIGINAL file when re-encoding would only
   *  cost a generation. */
  blob: Blob;
  filename: string;
  /** The ORIGINAL dimensions, for messaging — not the blob's. */
  width: number;
  height: number;
  /** Within ±1.5% of 16:9, so it inserts as a cover rather than letterboxed. */
  aspectOk: boolean;
  warnings: string[];
};

/**
 * Decode, downscale to fit 3840×2160, and re-encode an exported slide image.
 * Throws with a human message when the file can't be decoded.
 */
export async function encodeSlideImage(file: File): Promise<EncodedSlideImage> {
  let source: DecodedImage;
  try {
    source = await decode(file);
  } catch {
    throw new Error(`Couldn't read "${file.name}" as an image: export the slide as PNG or JPG.`);
  }
  const width = source.width || ('naturalWidth' in source ? source.naturalWidth : 0);
  const height = source.height || ('naturalHeight' in source ? source.naturalHeight : 0);
  if (!width || !height) throw new Error(`"${file.name}" has no readable dimensions.`);

  const aspectOk = Math.abs(width / height - TARGET) / TARGET <= ASPECT_TOLERANCE;
  const scale = Math.min(1, MAX_W / width, MAX_H / height);
  const outW = Math.round(width * scale);
  const outH = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  // getContext returns null when the browser refuses a context (out of memory,
  // too many live canvases). Unguarded that threw a bare TypeError from inside
  // drawImage; this says which file failed and why, like every other exit here.
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(`Couldn't prepare "${file.name}": the browser gave no drawing context.`);
  ctx.drawImage(source, 0, 0, outW, outH);

  let blob = await toBlob(canvas, 'image/webp', 0.88);
  let ext = '.webp';
  // Safari ignores the webp request and hands back PNG — fall back to JPEG.
  if (!blob || blob.type !== 'image/webp') {
    blob = await toBlob(canvas, 'image/jpeg', 0.9);
    ext = '.jpg';
  }
  if (!blob) throw new Error(`Couldn't re-encode "${file.name}": try a PNG or JPG export.`);

  // A small, already-efficient original (webp/jpg, not oversized) beats a
  // generational re-encode — keep it.
  if (/\.(webp|jpe?g)$/i.test(file.name) && scale === 1 && file.size <= blob.size) {
    blob = file;
    // The regex above already proved there is an extension; `?? ''` says so
    // without an assertion.
    ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase().replace('jpeg', 'jpg');
  }

  const stem = file.name.replace(/\.[^.]+$/, '');
  const warnings = [];
  if (width < 1920) {
    warnings.push(
      `This export is ${width}×${height}, below the 1920×1080 slide frame, so it may look soft. Export at 3840×2160 for sharp projection.`,
    );
  }
  if (blob.size > SIZE_WARN_BYTES) {
    warnings.push(
      `Encoded image is ${(blob.size / (1024 * 1024)).toFixed(1)}MB: it inlines into the deck file, so expect a heavy deck.`,
    );
  }

  return { blob, filename: stem + ext, width, height, aspectOk, warnings };
}
